import { PlusCircleOutlined } from '@ant-design/icons';
import { Modal, Select, Typography, Space, Row, Tag, Empty, Spin, AutoComplete, Input, message } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import fileExcelHistory from '@/assets/images/public/file-excel-history.svg';
import IconSearch from '@/assets/images/public/icon-search.svg';
import { getUserId } from '@/infrastructure/auth';
import { getProductId, getPid } from '@/infrastructure/product-info';
import { storageGet, storageSet } from '@/infrastructure/storage-utils';
import { getTagList, TagInfo } from '@/presentation/pages/setting/tag-manage/api';
import { getColorLabel } from '@/presentation/pages/setting/tag-manage/columns';
import { TAG_COLOR_INFO } from '@/presentation/pages/setting/tag-manage/config';
import { useStores } from '@/presentation/store';
import { MetricType } from '@/proto/metric';
import style from './index.module.scss';

interface IProps {
  visible: boolean;
  onSubmit: (tagName: string, tagColor: string, tagInfo: TagInfo | undefined) => void;
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  metricType?: MetricType;
}

interface OptionsType {
  label: string | React.ReactNode;
  value: string;
}

interface LocalTag {
  name: string;
  color: string;
  backgroundColor: string;
  borderColor: string;
}

export const AddTagModal: FC<IProps> = (props) => {
  const { visible, onSubmit, setModalVisible } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [tagList, setTagList] = useState<TagInfo[]>([]);
  const [options, setOptions] = useState<OptionsType[]>([]);
  const [tagName, setTagName] = useState<string>('');
  const [tagColor, setTagColor] = useState<string>(TAG_COLOR_INFO[0].color);
  const [open, setOpen] = useState<boolean | undefined>(undefined);
  const [localTagInfos, setLocalTagInfos] = useState<LocalTag[] | null>(null);
  const { getOptions } = useStores().IssueStore;

  const getTagInfo = () => tagList.find(item => item.tagName === tagName);

  const loadTagList = async () => {
    setLoading(true);
    const tagList = await getTagList();
    const options = tagList.map(item => ({ label: item.tagName, value: item.tagName, key: item.tagName }));
    setTagList(tagList);
    setOptions(options as OptionsType[]);
    setLoading(false);
  };

  const setInit = () => {
    const options = tagList.map(item => ({ label: item.tagName, value: item.tagName, key: item.tagName }));
    setTagName('');
    setTagColor(TAG_COLOR_INFO[0].color);
    setOptions(options as OptionsType[]);
  };

  const getLocalTags = () => {
    try {
      const localTagInfos = JSON.parse(storageGet(`${getUserId()}_tags`)!);
      const newLocalTagInfo = localTagInfos.map((localTagInfo: LocalTag) => {
        const tagColor = tagList?.find(tag => tag.tagName === localTagInfo.name)?.tagColor;
        const findTagInfo = TAG_COLOR_INFO.find(item => item.color === tagColor);
        return {
          ...localTagInfo,
          color: findTagInfo?.color ?? localTagInfo.color,
          backgroundColor: findTagInfo?.backgroundColor ?? localTagInfo.backgroundColor,
          borderColor: findTagInfo?.borderColor ?? localTagInfo.borderColor,
        };
      });
      setLocalTagInfos(newLocalTagInfo as LocalTag[]);
      storageSet(`${getUserId()}_tags`, JSON.stringify(newLocalTagInfo));
    } catch (e) {
      setLocalTagInfos(null);
    }
  };

  const setLocalStorageTag = () => {
    const tagInfo = localTagInfos || [];
    if (tagInfo?.find((item: any) => item.name === tagName) || !tagName) {
      return;
    }
    const findTagInfo = TAG_COLOR_INFO.find((item) => {
      const color = getTagInfo() ? getTagInfo()?.tagColor : tagColor;
      return item.color === color;
    }) ?? TAG_COLOR_INFO[0];
    storageSet(`${getUserId()}_tags`, JSON.stringify([...tagInfo, {
      name: tagName,
      color: findTagInfo?.color,
      backgroundColor: findTagInfo?.backgroundColor,
      borderColor: findTagInfo?.borderColor,
    }]));
  };

  const onSearch = (value: string) => {
    if (value.length > 30) {
      return;
    }
    const filterOptions = tagList.filter(item => item.tagName?.toLowerCase().indexOf(value.toLowerCase()) !== -1)
      .map(item => ({
        label: item.tagName,
        value: item.tagName,
        key: item.tagName,
      }));
    const findSameTag = tagList.some(item => item.tagName === value);
    const options = findSameTag
      ? filterOptions
      : [
        ...filterOptions,
        { label: getCreateTag(value), value, key: getCreateTag(value) },
      ];
    setOptions(options as OptionsType[]);
  };

  const colorOptions = TAG_COLOR_INFO.map(item => ({
    label: getColorLabel(item.color),
    value: item.color,
  }));

  const getCreateTag = (value: string) => <div
    style={{
      color: '#0A6CFF',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <div style={{ width: '350px' }} onClick={() => {
      setTagName(value);
      setOpen(undefined);
    }}>
      <PlusCircleOutlined style={{ marginRight: '8px' }}/>
      创建标签 {value}
    </div>
     <Select
      className={style.selectColor}
      options={colorOptions}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(undefined)}
      onChange={(color) => {
        setTagColor(color);
      }}
      defaultValue={TAG_COLOR_INFO[0].color}
     />
  </div>;

  useEffect(() => {
    if (visible) {
      setInit();
      loadTagList();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && !!tagList.length) {
      getLocalTags();
    }
  }, [visible, tagList]);

  return <Modal
    title={'添加标签'}
    visible={visible}
    onOk={async () => {
      await onSubmit(tagName, getTagInfo() ? getTagInfo()!.tagColor! : tagColor, getTagInfo());
      setLocalStorageTag();

      // 更新store的标签
      props.metricType && getOptions(props.metricType, true);
    }}
    onCancel={() => {
      setModalVisible(false);
    }}
  >
    <Spin spinning={loading}>
      <Space direction={'vertical'}  size={3} style={{ width: '100%' }}>
        <Row style={{ width: '100%', marginBottom: '10px' }}>
          <AutoComplete
            style={{ width: '465px' }}
            options={options}
            onChange={(tagName) => {
              if (tagName.length > 30) {
                message.warning('标签最多30个字符');
                return;
              }
              setTagName(tagName);
            }}
            onSearch={value => onSearch(value)}
            optionFilterProp={'label'}
            value={tagName}
            showSearch
            open={open}
          >
            <Input suffix={<img src={IconSearch} alt={''}/>} placeholder={'搜索或创建标签'}/>
          </AutoComplete>
        </Row>
        <Row style={{ width: '100%' }}>
          <Typography.Text style={{ display: 'flex', alignItems: 'center' }}>
            <img src={fileExcelHistory} alt={''}/>
            &nbsp;最近使用标签
          </Typography.Text>
        </Row>
        <Row>
          {!!localTagInfos?.length ? localTagInfos.map((item: any) => <Tag
            onClick={() => {
              setTagName(item.name);
              setTagColor(item.color);
            }}
            style={{
              backgroundColor: item.backgroundColor,
              color: item.color,
              borderColor: item.borderColor,
              marginRight: '12px',
              marginTop: '10px',
              cursor: 'pointer',
            }}>
            {item.name}
          </Tag>) : <Empty description={'暂无最近使用标签'} style={{ width: '100%' }}/>}
        </Row>
        <Row style={{ marginTop: '100px' }}>
          <a
            style={{ textDecoration: 'underline' }}
            href={`${location.origin}/v2/setting/product-config?tab=tag&productId=${getProductId()}&pid=${getPid()}`}
          >
            查看标签管理
          </a>
        </Row>
      </Space>
    </Spin>
  </Modal>;
};
